<template>
  <!-- <tbody> -->
  <tr class="text-sm hover:bg-gray-50 cursor-pointer" @click="expanded = !expanded">
    <td class="py-2 px-3 border border-l-0" />
    <td class="py-2 px-3 border w-px align-top">
      <svg viewBox="0 0 24 24" class="w-5 h-5 inline-block text-gray-500" fill="currentColor">
        <path fill="none" d="M0 0h24v24H0z" />
        <path
          v-if="expanded"
          d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10
            10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0
            16zm-5-9h10v2H7v-2z"
        />
        <path
          v-else
          d="M11 11V7h2v4h4v2h-4v4h-2v-4H7v-2h4zm1 11C6.477 22 2 17.523
            2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0
            0-16 8 8 0 0 0 0 16z"
        />
      </svg>
    </td>
    <td class="py-2 px-3 align-top border">
      <ShowFacebookCampaignOverviewAction :facebook-campaign="facebookCampaign" />
      <template v-if="expanded">
        <Portal :to="`portal-facebook-campaign-${facebookCampaign.id}`">
          <tr class="text-sm text-gray-600 bg-gray-100">
            <td class="py-2 px-3 border border-l-0" />
            <td class="py-2 px-3 border w-px" />
            <td class="py-2 px-3 border text-left">
              <span class="inline-block"> Ad name </span>
            </td>
            <td class="py-2 px-3 border text-right">Impressions</td>
            <td class="py-2 px-3 border text-right">Reach</td>
            <td class="py-2 px-3 border text-right">Frequency</td>
            <td class="py-2 px-3 border text-right">Pageviews</td>
            <td class="py-2 px-3 border text-right">Contact Requests</td>
            <td class="py-2 px-3 border border-r-0 text-right">Spend</td>
          </tr>
        </Portal>
        <template v-for="facebookAd in facebookCampaign.facebook_ads">
          <Portal
            :to="`portal-facebook-campaign-${facebookCampaign.id}-ad-${facebookAd.id}`"
            :key="`${facebookCampaign.id}:${facebookAd.id}`"
          >
            <FacebookAdTableRow
              :ad="facebookAd"
              :ad-insights="facebookCampaign.facebook_ad_insights"
            />
          </Portal>
        </template>
      </template>
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ facebookCampaignTotalsFormatted.impressions }}
      <br />
      {{ facebookCampaignTotalsFormatted.cpi }}
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ facebookCampaignTotalsFormatted.reach }}
      <br />
      {{ facebookCampaignTotalsFormatted.cpr }}
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ frequency }}
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ facebookCampaignTotalsFormatted.inlineLinkClicks }}
      <br />
      {{ pageviewsPerImpression }}% / {{ spendPerPageView }}
    </td>
    <td class="py-2 align-top px-3 border text-right">
      {{ facebookCampaignTotalsFormatted.offsiteConversions }} ({{ offsiteConversionsPerPageview }})
    </td>
    <td class="py-2 align-top px-3 border border-r-0 text-right">
      {{ facebookCampaignTotalsFormatted.spend }}
    </td>
  </tr>
</template>

<script>
import Numbro from '@/utils/numbro';
import FacebookAdTableRow from './FacebookAdTableRow.vue';
import ShowFacebookCampaignOverviewAction from './ShowFacebookCampaignOverviewAction.vue';

export default {
  components: {
    FacebookAdTableRow,
    ShowFacebookCampaignOverviewAction,
  },
  props: {
    facebookCampaign: { type: Object, required: true },
    facebookCampaignReports: { type: Array, default: () => [] },
  },
  data() {
    return {
      expanded: false,
    };
  },
  computed: {
    facebookCampaignTotals() {
      return this.facebookCampaign.facebook_campaign_insights.reduce(
        (acc, current) => ({
          impressions: acc.impressions + (current?.impressions || 0),
          reach: acc.reach + (current?.reach || 0),
          clicks: acc.clicks + (current?.clicks || 0),
          inlineLinkClicks: acc.inlineLinkClicks + (current?.inline_link_clicks || 0),
          offsiteConversions: acc.offsiteConversions + (current?.offsiteConversions || 0),
          spend: acc.spend + (current?.spend || 0),
        }),
        {
          impressions: 0,
          reach: 0,
          clicks: 0,
          inlineLinkClicks: 0,
          offsiteConversions: 0,
          spend: 0,
        }
      );
    },
    facebookCampaignTotalsFormatted() {
      const impressions = this.facebookCampaignTotals?.impressions || 0;
      const reach = this.facebookCampaignTotals?.reach || 0;
      const spend = (this.facebookCampaignTotals?.spend || 0) / 100;
      const cpi = impressions ? 1000 * (spend / impressions) : 0;
      const cpr = reach ? 1000 * (spend / reach) : 0;

      return {
        impressions: Numbro(impressions).format({ thousandSeparated: true }),
        cpi: Numbro(cpi).formatCurrency({ thousandSeparated: true, mantissa: 2 }),
        reach: Numbro(reach).format({ thousandSeparated: true }),
        cpr: Numbro(cpr).formatCurrency({ thousandSeparated: true, mantissa: 2 }),
        clicks: Numbro(this.facebookCampaignTotals?.clicks || 0).format({
          thousandSeparated: true,
        }),
        inlineLinkClicks: Numbro(this.facebookCampaignTotals?.inlineLinkClicks || 0).format({
          thousandSeparated: true,
        }),
        offsiteConversions: Numbro(this.facebookCampaignTotals?.offsiteConversions || 0).format({
          thousandSeparated: true,
        }),
        spend: Numbro(spend).formatCurrency({
          thousandSeparated: true,
          mantissa: 2,
        }),
      };
    },
    frequency() {
      const { reach, impressions } = this.facebookCampaignTotals;
      if (!reach) return 0;
      return Numbro(impressions / reach).format({ thousandSeparated: true, mantissa: 2 });
    },
    pageviewsPerImpression() {
      const { impressions, inlineLinkClicks } = this.facebookCampaignTotals;
      if (!impressions) return 0;
      return Numbro((inlineLinkClicks / impressions) * 100).format({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
    offsiteConversionsPerPageview() {
      const { inlineLinkClicks, offsiteConversions } = this.facebookCampaignTotals;
      if (!inlineLinkClicks) return 0;
      return Numbro(offsiteConversions / inlineLinkClicks).format({
        output: 'percent',
        mantissa: 1,
      });
    },
    spendPerPageView() {
      const { inlineLinkClicks, spend } = this.facebookCampaignTotals;
      if (!inlineLinkClicks) return 0;
      return Numbro(spend / 100 / inlineLinkClicks).formatCurrency({
        thousandSeparated: true,
        mantissa: 2,
      });
    },
  },
};
</script>
